import React from "react";

import {
  Hidden,
  Stack,
  Heading,
  Columns,
  Column,
  Text,
  ColumnsProps,
  Box,
} from "@origin-digital/ods-core";
import {
  LinkChevron,
  AppleStoreLink,
  AndroidStoreLink,
} from "@origin-digital/style-guide";

export interface ICallToActionProps {
  text: string;
  href: string;
}

export interface IBenefitsProps {
  heading: string;
}

export interface IBenefitProps {
  heading: string;
  text: string;
  cta?: ICallToActionProps;
  reverse?: boolean;
  apps?: boolean;
  top?: boolean;
}

const CallToAction: React.FC<ICallToActionProps> = ({ text, href }) => (
  <LinkChevron href={href} text={text} data-id="call-to-action" />
);

const Apps: React.FC = () => (
  <Box data-id="apps">
    <Columns space="medium">
      <Column width="content">
        <AppleStoreLink />
      </Column>
      <Column width="content">
        <AndroidStoreLink />
      </Column>
    </Columns>
  </Box>
);

const Content: React.FC<Pick<
  IBenefitProps,
  "heading" | "text" | "cta" | "apps"
>> = ({ heading, text, cta, apps }) => (
  <Stack space="medium" alignX="left">
    <Heading variant="h3">{heading}</Heading>
    <Text>{text}</Text>

    {cta && <CallToAction {...cta} />}
    {apps && <Apps />}
  </Stack>
);

const Layout: React.FC<IBenefitProps & Pick<ColumnsProps, "alignY">> = ({
  alignY,
  reverse,
  children,
  ...props
}) => (
  <>
    <Hidden above="sm">
      <Stack space="large">
        {children}
        <Content {...props} />
      </Stack>
    </Hidden>

    <Hidden below="md">
      <Columns space="xlarge" alignY={alignY}>
        {reverse ? (
          <Column width="1/2">
            <Content {...props} />
          </Column>
        ) : (
          <div />
        )}

        <Column width="1/2">{children}</Column>

        {!reverse ? (
          <Column width="1/2">
            <Content {...props} />
          </Column>
        ) : (
          <div />
        )}
      </Columns>
    </Hidden>
  </>
);

export const Benefits: React.FC<IBenefitsProps> = ({ heading, children }) => (
  <>
    {/* LG */}
    <Hidden below="xl">
      <Stack space="small">
        <Heading variant="h2" align="center">
          {heading}
        </Heading>

        <Columns>
          <Column width="1/12" />
          <Column width="5/6">
            <Stack space="xxlarge">{children}</Stack>
          </Column>
        </Columns>
      </Stack>
    </Hidden>

    {/* SM / MD */}
    <Hidden above="lg">
      <Hidden below="md">
        <Stack space="small">
          <Heading variant="h2" align="center">
            {heading}
          </Heading>

          {children}
        </Stack>
      </Hidden>
    </Hidden>

    {/* XS */}
    <Hidden above="sm">
      <Stack space="xlarge">
        <Heading variant="h2">{heading}</Heading>

        {children}
      </Stack>
    </Hidden>
  </>
);

export const Benefit: React.FC<IBenefitProps> = ({ top, ...props }) => (
  <Stack>
    <Hidden above="md">
      <Layout {...props} alignY="top" />
    </Hidden>

    <Hidden below="lg">
      <Layout {...props} alignY={top ? "top" : "center"} />
    </Hidden>
  </Stack>
);
